import { ensureProtocol } from '~/modules/url';

/**
 * Wraps bare URLs in a trusted HTML string with anchor tags, so a CMS author
 * who pastes a raw link (e.g. a product's "Care guide" URL) gets a clickable
 * link instead of inert text. Built for `dangerouslySetInnerHTML` content:
 *
 *  - only `http(s)://` and `www.` URLs match, so it can never synthesise a
 *    `javascript:`/`data:` href;
 *  - text inside an existing `<a>` element is skipped, so real CMS links are
 *    never double-wrapped;
 *  - it only rewrites text *between* tags (the input is split on tags), so URLs
 *    sitting in attributes like `href="…"` are left exactly as authored.
 */

// A bare URL in a run of text. Stops at whitespace, a tag edge or a quote, so it
// can't swallow following markup. Trailing sentence punctuation is trimmed
// separately.
const URL_IN_TEXT = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

/**
 * Trim trailing punctuation that reads as sentence punctuation rather than part
 * of the link. `.`, `,`, `!`, `?` always trim. `)`/`]` only trim when the URL
 * has more closers than openers (unbalanced) — so a real path segment like
 * `…_(disambiguation)` keeps its closing paren while `(see https://x.com)` does
 * not. `;` is never trimmed, so an entity inside the URL (`&amp;`) stays intact.
 */
const trimTrailingPunctuation = (url: string): string => {
    let end = url.length;
    while (end > 0) {
        const char = url[end - 1];
        if (char === '.' || char === ',' || char === '!' || char === '?') {
            end -= 1;
            continue;
        }
        if (char === ')' || char === ']') {
            const opener = char === ')' ? '(' : '[';
            const slice = url.slice(0, end);
            const openers = slice.split(opener).length - 1;
            const closers = slice.split(char).length - 1;
            if (closers > openers) {
                end -= 1;
                continue;
            }
        }
        break;
    }
    return url.slice(0, end);
};

const linkifyTextSegment = (text: string): string =>
    text.replace(URL_IN_TEXT, (match) => {
        const url = trimTrailingPunctuation(match);
        const trailing = match.slice(url.length);
        const href = ensureProtocol(url);
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>${trailing}`;
    });

export const linkifyHtml = (html: string): string => {
    // Cheap bail-out: the overwhelming majority of CMS values carry no URL.
    // Case-insensitive to match the URL regex (an uppercase `HTTP://`/`WWW.`
    // would otherwise be skipped).
    const lower = html.toLowerCase();
    if (!lower.includes('http') && !lower.includes('www.')) return html;
    let anchorDepth = 0;
    return html
        .split(/(<[^>]*>)/)
        .map((token) => {
            if (token.startsWith('<')) {
                // Track only `<a>` (not `<article>`, `<aside>`, …) so URLs inside
                // an existing link are left alone.
                if (/^<a[\s/>]/i.test(token)) anchorDepth += 1;
                else if (/^<\/a\s*>/i.test(token))
                    anchorDepth = Math.max(0, anchorDepth - 1);
                return token;
            }
            return anchorDepth > 0 ? token : linkifyTextSegment(token);
        })
        .join('');
};
